/** Phase 6: the in-app help assistant (floating "?" button on every page). */
import { useEffect, useRef, useState } from "react";
import type { HelpAnswer } from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";

interface Msg { role: "user" | "assistant"; text: string; ai?: boolean; sources?: string[] }

const SUGGEST: Record<string, string[]> = {
  player: ["How do I talk to the DM?", "How do I level up?", "How does loot work?"],
  staff: ["How do I start a session?", "How do I run a pre-built adventure?", "How does the cost multiplier work?", "How do I rewind the DM?"],
};

/** Very small markdown: **bold**, numbered/bulleted lines, paragraphs. */
function Rich({ text }: { text: unknown }) {
  const t = typeof text === "string" ? text : text == null ? "" : JSON.stringify(text);
  return (
    <>
      {t.split(/\n{2,}/).map((p, i) => (
        <p key={i}>
          {p.split("\n").map((line, j) => (
            <span key={j}>
              {j > 0 && <br />}
              {line.split(/(\*\*[^*]+\*\*)/).map((part, k) => (part.startsWith("**") && part.endsWith("**") ? <strong key={k}>{part.slice(2, -2)}</strong> : part))}
            </span>
          ))}
        </p>
      ))}
    </>
  );
}

export function HelpButton() {
  const { me } = useApp();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement | null>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, busy]);
  const staff = me.role === "host" || me.role === "cohost" || me.isAdmin;
  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    const history = msgs.slice(-6).map((m) => ({ role: m.role, text: m.text }));
    setMsgs((m) => [...m, { role: "user", text }]);
    setQ("");
    setBusy(true);
    try {
      const page = /^#\/(\w+)/.exec(window.location.hash)?.[1] ?? "table";
      const r: HelpAnswer = await api.help({ question: text, history, campaignId: me.campaign?.id, page });
      setMsgs((m) => [...m, { role: "assistant", text: typeof r?.answer === "string" ? r.answer : "The help assistant sent back something I couldn't show. Try asking again.", ai: !!r?.ai, sources: Array.isArray(r?.sources) ? r.sources : [] }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: err instanceof Error ? err.message : String(err), ai: false }]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button className="help-fab" aria-label="Help" title="Help" onClick={() => setOpen(!open)}>{open ? "×" : "?"}</button>
      {open && (
        <div className="help-panel" role="dialog" aria-label="Help assistant">
          <div className="help-head"><strong>Help</strong><span className="muted small">Ask how to do anything here</span></div>
          <div className="help-msgs">
            {!msgs.length && (
              <div className="help-suggest">
                {(staff ? [...SUGGEST.staff!, ...SUGGEST.player!.slice(0, 2)] : SUGGEST.player!).map((s) => (
                  <button key={s} className="link" onClick={() => ask(s)}>{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`help-msg ${m.role}`}>
                <Rich text={m.text} />
                {m.role === "assistant" && m.ai === false && <span className="muted small">From the help pages (no AI used).</span>}
              </div>
            ))}
            {busy && <div className="help-msg assistant muted">Thinking…</div>}
            <div ref={end} />
          </div>
          <form className="help-form" onSubmit={(e) => { e.preventDefault(); void ask(q); }}>
            <input value={q} maxLength={600} placeholder="e.g. How do I add an NPC voice?" onChange={(e) => setQ(e.target.value)} aria-label="Your question" />
            <button className="primary" disabled={busy || q.trim().length < 2}>Ask</button>
          </form>
          {me.campaign && <p className="muted small help-note">Answers use Grok on {me.campaign.name}'s AI key (a fraction of a cent each).</p>}
        </div>
      )}
    </>
  );
}
